/**
 * Turns the segmenter's confidence mask into a drawable alpha stencil.
 *
 * The model emits 256×256 floats; the frame is up to 1280×720. Rather than
 * resampling in JavaScript we write the mask into a small canvas ALPHA channel
 * and let the GPU upscale it during `drawImage` — that is a free bilinear
 * filter, and it is also what gives the cut-out its soft edge.
 */
export interface MaskStencil {
  readonly canvas: HTMLCanvasElement;
  /** Copies a fresh confidence mask in. Returns false if the canvas is dead. */
  write(mask: Float32Array, width: number, height: number): boolean;
}

/** Below this confidence a pixel is background; above it, fully the subject. */
const LOW = 0.35;
const HIGH = 0.65;

const RANGE = HIGH - LOW;

export function createMaskStencil(): MaskStencil {
  const canvas = document.createElement('canvas');
  let ctx = canvas.getContext('2d');
  let image: ImageData | null = null;

  return {
    canvas,
    write(mask, width, height) {
      if (!ctx || width === 0 || height === 0) return false;
      if (canvas.width !== width || canvas.height !== height) {
        canvas.width = width;
        canvas.height = height;
        ctx = canvas.getContext('2d');
        image = null;
        if (!ctx) return false;
      }
      if (!image || image.width !== width || image.height !== height) {
        image = ctx.createImageData(width, height);
      }

      const pixels = image.data;
      for (let i = 0; i < mask.length; i += 1) {
        // A hard 0/1 threshold shimmers frame to frame; a narrow ramp around the
        // decision boundary is what stops the outline crawling.
        const value = mask[i] ?? 0;
        const alpha = value <= LOW ? 0 : value >= HIGH ? 1 : (value - LOW) / RANGE;
        const offset = i * 4;
        pixels[offset] = 255;
        pixels[offset + 1] = 255;
        pixels[offset + 2] = 255;
        pixels[offset + 3] = Math.round(alpha * 255);
      }
      ctx.putImageData(image, 0, 0);
      return true;
    },
  };
}
